import type { Severity } from '../types/common.js';
import type { DocumentProfile, ProfileRegistry, ResolvedRule } from '../types/profile.js';

/**
 * Profile loading and validation.
 *
 * Document rules are data. This module is the only gate between raw JSON and the
 * engine, so a typo in a profile fails loudly at startup/build time instead of
 * silently turning a check into a pass.
 */

export interface ProfileIssue {
  /** JSON path, e.g. `rules.head_size.params.min`. */
  path: string;
  message: string;
}

export interface ProfileValidationResult {
  profile?: DocumentProfile;
  issues: ProfileIssue[];
  warnings: ProfileIssue[];
}

/** Checks whose numeric bounds must be labelled official vs policy. */
const BOUNDS_MATTER = new Set([
  'head_size',
  'eye_line',
  'file_size',
  'image_size',
  'aspect_ratio',
  'face_count',
]);

const REQUIRED_RULE_IDS_HINT = [
  'file_format',
  'file_size',
  'image_size',
  'aspect_ratio',
  'face_count',
  'face_position',
  'head_size',
  'eye_line',
  'face_pose',
  'eye_open',
  'head_coverage',
  'background',
  'sharpness',
  'exposure',
  'lighting',
  'noise',
  'compression_artifacts',
  'glasses',
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const roundRatio = (value: number): string => value.toFixed(3);

export const validateProfile = (raw: unknown): ProfileValidationResult => {
  const issues: ProfileIssue[] = [];
  const warnings: ProfileIssue[] = [];
  const fail = (path: string, message: string): void => {
    issues.push({ path, message });
  };
  const warn = (path: string, message: string): void => {
    warnings.push({ path, message });
  };

  if (!isRecord(raw)) {
    return { issues: [{ path: '', message: 'Профиль должен быть JSON-объектом.' }], warnings };
  }

  if (raw.schemaVersion !== '1.0') fail('schemaVersion', 'Ожидается "1.0".');
  if (!isNonEmptyString(raw.id)) fail('id', 'Обязательное поле.');
  else if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(raw.id as string)) {
    fail('id', 'Используйте kebab-case: a-z, 0-9 и дефис.');
  }
  for (const key of [
    'name',
    'sourceUrl',
    'sourceCheckedAt',
    'disclaimer',
  ] as const) {
    if (!isNonEmptyString(raw[key])) fail(key, 'Обязательное поле.');
  }
  if (isNonEmptyString(raw.sourceUrl) && !/^https?:\/\//.test(raw.sourceUrl as string)) {
    fail('sourceUrl', 'Должен быть абсолютным http(s) URL официального источника.');
  }
  if (isNonEmptyString(raw.sourceCheckedAt) && !ISO_DATE.test(raw.sourceCheckedAt as string)) {
    fail('sourceCheckedAt', 'Формат YYYY-MM-DD.');
  }
  if (raw.verificationNote !== undefined && !isNonEmptyString(raw.verificationNote)) {
    fail('verificationNote', 'Если поле задано, оно должно быть непустой строкой.');
  }
  if (raw.aliases !== undefined && !Array.isArray(raw.aliases)) {
    fail('aliases', 'Ожидается массив строк.');
  }

  // --- image requirements -------------------------------------------------
  if (!isRecord(raw.image)) {
    fail('image', 'Обязательный объект с требованиями к файлу и размерам.');
  } else {
    const image = raw.image;
    for (const key of ['minWidth', 'maxWidth', 'minHeight', 'maxHeight', 'maxFileSize'] as const) {
      if (!isFiniteNumber(image[key])) fail(`image.${key}`, 'Ожидается число.');
    }
    if (isFiniteNumber(image.minWidth) && isFiniteNumber(image.maxWidth) && image.minWidth > image.maxWidth) {
      fail('image.minWidth', 'minWidth больше maxWidth.');
    }
    if (isFiniteNumber(image.minHeight) && isFiniteNumber(image.maxHeight) && image.minHeight > image.maxHeight) {
      fail('image.minHeight', 'minHeight больше maxHeight.');
    }
    if (image.minFileSize !== undefined && !isFiniteNumber(image.minFileSize)) {
      fail('image.minFileSize', 'Ожидается число.');
    }
    if (isFiniteNumber(image.maxFileSize) && image.maxFileSize <= 0) {
      fail('image.maxFileSize', 'Должен быть положительным.');
    }
    if (!isRecord(image.aspectRatio) || !isFiniteNumber(image.aspectRatio.min) || !isFiniteNumber(image.aspectRatio.max)) {
      fail('image.aspectRatio', 'Ожидается { min, max }.');
    }
    if (!Array.isArray(image.formats) || image.formats.length === 0) {
      fail('image.formats', 'Ожидается непустой массив форматов.');
    }

    // Справочный размер стандарта — только метаданные документа. Из миллиметров
    // никогда не выводятся пиксели, DPI или что-либо ещё: цифровые границы
    // объявлены отдельно и обязательны.
    if (image.nominalPhotoSize !== undefined) {
      const nominal = image.nominalPhotoSize;
      if (!isRecord(nominal) || !isFiniteNumber(nominal.widthMm) || !isFiniteNumber(nominal.heightMm)) {
        fail('image.nominalPhotoSize', 'Ожидается { widthMm, heightMm }.');
      } else if (nominal.widthMm <= 0 || nominal.heightMm <= 0) {
        fail('image.nominalPhotoSize', 'Размеры должны быть положительными.');
      }
    }
    if (image.requiredAspectRatio !== undefined) {
      const ratio = image.requiredAspectRatio;
      if (!isRecord(ratio) || !isFiniteNumber(ratio.width) || !isFiniteNumber(ratio.height)) {
        fail('image.requiredAspectRatio', 'Ожидается { width, height } — соотношение сторон как дробь.');
      } else if (ratio.width <= 0 || ratio.height <= 0) {
        fail('image.requiredAspectRatio', 'Числитель и знаменатель должны быть положительными.');
      } else if (isRecord(image.aspectRatio) && isFiniteNumber(image.aspectRatio.min) && isFiniteNumber(image.aspectRatio.max)) {
        const target = ratio.width / ratio.height;
        if (target < image.aspectRatio.min - 0.001 || target > image.aspectRatio.max + 0.001) {
          fail(
            'image.requiredAspectRatio',
            `Соотношение ${roundRatio(target)} не попадает в допуск aspectRatio ${image.aspectRatio.min}–${image.aspectRatio.max}.`,
          );
        }
      }
    }
    if (image.aspectRatioNote !== undefined && !isNonEmptyString(image.aspectRatioNote)) {
      fail('image.aspectRatioNote', 'Если поле задано, оно должно быть непустой строкой.');
    }
    if (image.digital !== undefined) {
      const digital = image.digital;
      if (!isRecord(digital)) {
        fail('image.digital', 'Ожидается объект.');
      } else {
        for (const key of ['minWidth', 'minHeight', 'maxWidth', 'maxHeight'] as const) {
          if (!isFiniteNumber(digital[key])) fail(`image.digital.${key}`, 'Ожидается число.');
        }
        // Пиксельные ограничения не должны противоречить каноническим полям.
        for (const [digitalKey, flatKey] of [
          ['minWidth', 'minWidth'],
          ['maxWidth', 'maxWidth'],
          ['minHeight', 'minHeight'],
          ['maxHeight', 'maxHeight'],
        ] as const) {
          if (isFiniteNumber(digital[digitalKey]) && isFiniteNumber(image[flatKey]) && digital[digitalKey] !== image[flatKey]) {
            fail(`image.digital.${digitalKey}`, `Не совпадает с image.${flatKey} (${String(image[flatKey])}).`);
          }
        }
        if (digital.dpi !== undefined && !isFiniteNumber(digital.dpi)) {
          fail('image.digital.dpi', 'Ожидается число.');
        }
      }
    }

  }

  // --- rules --------------------------------------------------------------
  if (!isRecord(raw.rules)) {
    fail('rules', 'Обязательный объект с правилами.');
  } else {
    const rules = raw.rules;
    const ids = Object.keys(rules);
    if (ids.length === 0) fail('rules', 'Нужно хотя бы одно правило.');
    for (const id of ids) {
      const rule = rules[id];
      if (!isRecord(rule)) {
        fail(`rules.${id}`, 'Ожидается объект.');
        continue;
      }
      if (rule.enabled !== undefined && typeof rule.enabled !== 'boolean') {
        fail(`rules.${id}.enabled`, 'Ожидается boolean.');
      }
      if (
        rule.severity !== undefined &&
        rule.severity !== 'info' &&
        rule.severity !== 'warning' &&
        rule.severity !== 'error'
      ) {
        fail(`rules.${id}.severity`, 'Ожидается info | warning | error.');
      }
      if (rule.params !== undefined && !isRecord(rule.params)) {
        fail(`rules.${id}.params`, 'Ожидается объект.');
      }
      const params = isRecord(rule.params) ? rule.params : {};
      const min = params.min;
      const max = params.max;
      if (isFiniteNumber(min) && isFiniteNumber(max) && min > max) {
        fail(`rules.${id}.params`, 'min больше max.');
      }

      // official bounds: what the authority publishes
      if (rule.official !== undefined) {
        if (!isRecord(rule.official)) {
          fail(`rules.${id}.official`, 'Ожидается объект.');
        } else {
          const official = rule.official;
          if (official.min !== undefined && !isFiniteNumber(official.min)) {
            fail(`rules.${id}.official.min`, 'Ожидается число.');
          }
          if (official.max !== undefined && !isFiniteNumber(official.max)) {
            fail(`rules.${id}.official.max`, 'Ожидается число.');
          }
          if (isFiniteNumber(official.min) && isFiniteNumber(official.max) && official.min > official.max) {
            fail(`rules.${id}.official`, 'min больше max.');
          }
          if (official.statement !== undefined && !isNonEmptyString(official.statement)) {
            fail(`rules.${id}.official.statement`, 'Если поле задано, оно должно быть непустой строкой.');
          }
          // A mirrored flat bound must not contradict the official one.
          if (isFiniteNumber(official.min) && isFiniteNumber(params.min) && official.min !== params.min) {
            warn(`rules.${id}`, 'params.min не совпадает с official.min: проверка использует official, а params остаётся для совместимости.');
          }
          if (isFiniteNumber(official.max) && isFiniteNumber(params.max) && official.max !== params.max) {
            warn(`rules.${id}`, 'params.max не совпадает с official.max.');
          }
        }
      }

      // measurement uncertainty
      if (rule.measurement !== undefined) {
        if (!isRecord(rule.measurement)) {
          fail(`rules.${id}.measurement`, 'Ожидается объект.');
        } else {
          const measurement = rule.measurement;
          if (measurement.tolerance !== undefined && (!isFiniteNumber(measurement.tolerance) || measurement.tolerance < 0)) {
            fail(`rules.${id}.measurement.tolerance`, 'Ожидается неотрицательное число.');
          }
          const confidence = measurement.uncertainBelowConfidence;
          if (confidence !== undefined && (!isFiniteNumber(confidence) || confidence < 0 || confidence > 1)) {
            fail(`rules.${id}.measurement.uncertainBelowConfidence`, 'Ожидается число 0…1.');
          }
        }
      }

      // internal policy: must never be presented as an official requirement
      if (rule.policy !== undefined) {
        if (!isRecord(rule.policy)) {
          fail(`rules.${id}.policy`, 'Ожидается объект.');
        } else {
          const policy = rule.policy;
          if (policy.min !== undefined && !isFiniteNumber(policy.min)) {
            fail(`rules.${id}.policy.min`, 'Ожидается число.');
          }
          if (policy.max !== undefined && !isFiniteNumber(policy.max)) {
            fail(`rules.${id}.policy.max`, 'Ожидается число.');
          }
          if (isFiniteNumber(policy.min) && isFiniteNumber(policy.max) && policy.min > policy.max) {
            fail(`rules.${id}.policy`, 'min больше max.');
          }
          if (policy.onViolation !== undefined && policy.onViolation !== 'warning' && policy.onViolation !== 'error') {
            fail(`rules.${id}.policy.onViolation`, 'Ожидается warning | error.');
          }
          if (policy.reason !== undefined && !isNonEmptyString(policy.reason)) {
            fail(`rules.${id}.policy.reason`, 'Если поле задано, оно должно быть непустой строкой.');
          }
          if (policy.onViolation !== undefined && !isNonEmptyString(policy.reason)) {
            warn(`rules.${id}.policy.reason`, 'Для внутреннего порога желательно объяснить причину: пользователь видит её в интерфейсе.');
          }
        }
      }

      // Numeric thresholds must be labelled: official or policy.
      if (
        rule.official === undefined &&
        rule.policy === undefined &&
        BOUNDS_MATTER.has(id) &&
        (isFiniteNumber(params.min) || isFiniteNumber(params.max))
      ) {
        warn(`rules.${id}`, 'Числовые пороги не помечены как official или policy: непонятно, требование это документа или политика сервиса.');
      }
    }
    for (const expected of REQUIRED_RULE_IDS_HINT) {
      if (!(expected in rules)) {
        warn(`rules.${expected}`, 'Правило отсутствует: проверка не будет выполнена для этого документа.');
      }
    }
  }

  // --- provenance ---------------------------------------------------------
  if (!isRecord(raw.provenance)) {
    fail('provenance', 'Обязательный блок: источник требований (sourceName, sourceUrl, checkedAt, requirementsVersion).');
  } else {
    const provenance = raw.provenance;
    if (!isNonEmptyString(provenance.sourceName)) fail('provenance.sourceName', 'Обязательное поле.');
    if (!isNonEmptyString(provenance.sourceUrl)) fail('provenance.sourceUrl', 'Обязательное поле.');
    else if (!/^https?:\/\//.test(provenance.sourceUrl as string)) {
      fail('provenance.sourceUrl', 'Должен быть абсолютным http(s) URL.');
    }
    if (!isNonEmptyString(provenance.checkedAt)) fail('provenance.checkedAt', 'Обязательное поле.');
    else if (!ISO_DATE.test(provenance.checkedAt as string)) fail('provenance.checkedAt', 'Формат YYYY-MM-DD.');
    if (!isNonEmptyString(provenance.requirementsVersion)) {
      fail('provenance.requirementsVersion', 'Обязательное поле: редакция требований, которую проверяли.');
    }
    if (
      provenance.status !== undefined &&
      provenance.status !== 'verified' &&
      provenance.status !== 'needs-source-review'
    ) {
      fail('provenance.status', 'Ожидается verified | needs-source-review.');
    }
    if (isNonEmptyString(provenance.sourceUrl) && isNonEmptyString(raw.sourceUrl) && provenance.sourceUrl !== raw.sourceUrl) {
      fail('provenance.sourceUrl', 'Должен совпадать с sourceUrl профиля.');
    }
    if (isNonEmptyString(provenance.checkedAt) && isNonEmptyString(raw.sourceCheckedAt) && provenance.checkedAt !== raw.sourceCheckedAt) {
      fail('provenance.checkedAt', 'Должен совпадать с sourceCheckedAt профиля.');
    }
    if (provenance.secondarySources !== undefined) {
      if (!Array.isArray(provenance.secondarySources)) {
        fail('provenance.secondarySources', 'Ожидается массив.');
      } else {
        provenance.secondarySources.forEach((entry: unknown, index: number) => {
          if (!isRecord(entry) || !isNonEmptyString(entry.name) || !isNonEmptyString(entry.url)) {
            fail(`provenance.secondarySources[${index}]`, 'Ожидается { name, url }.');
          }
        });
      }
    }
    if (provenance.note !== undefined && !isNonEmptyString(provenance.note)) {
      fail('provenance.note', 'Если поле задано, оно должно быть непустой строкой.');
    }
  }

  // --- image size: warnBelowBytes is a policy threshold -------------------
  if (isRecord(raw.image) && raw.image.minFileSize !== undefined && raw.image.warnBelowBytes !== undefined) {
    warn('image.warnBelowBytes', 'Заданы и minFileSize, и warnBelowBytes: minFileSize — официальный минимум, warnBelowBytes — внутренний порог.');
  }

  // --- visual presentation ------------------------------------------------
  if (!isRecord(raw.visual)) {
    fail('visual', 'Обязательный объект (флаги отображения направляющих).');
  } else {
    for (const key of ['showCenterLine', 'showEyeLine', 'showHeadZone'] as const) {
      if (typeof raw.visual[key] !== 'boolean') fail(`visual.${key}`, 'Ожидается boolean.');
    }
  }

  // --- CTA ----------------------------------------------------------------
  if (!isRecord(raw.cta)) {
    fail('cta', 'Обязательный объект.');
  } else {
    if (!isNonEmptyString(raw.cta.url)) fail('cta.url', 'Обязательное поле (ссылка на основной продукт).');
    if (!isNonEmptyString(raw.cta.label)) fail('cta.label', 'Обязательное поле.');
  }

  // --- SEO ----------------------------------------------------------------
  if (raw.seo !== undefined) {
    if (!isRecord(raw.seo)) {
      fail('seo', 'Ожидается объект.');
    } else {
      if (!isNonEmptyString(raw.seo.slug)) fail('seo.slug', 'Обязательное поле.');
      for (const key of ['title', 'description', 'h1'] as const) {
        if (!isNonEmptyString(raw.seo[key])) fail(`seo.${key}`, 'Обязательное поле.');
      }
      if (raw.seo.faq !== undefined && !Array.isArray(raw.seo.faq)) {
        fail('seo.faq', 'Ожидается массив.');
      }
    }
  } else {
    warn('seo', 'Нет SEO-блока: страница документа не будет сгенерирована.');
  }

  if (issues.length > 0) return { issues, warnings };
  return { profile: raw as unknown as DocumentProfile, issues, warnings };
};

/** Loads and validates a batch of raw profiles. */
export const loadProfiles = (
  rawProfiles: unknown[],
): { profiles: DocumentProfile[]; issues: Array<{ profile: string; issues: ProfileIssue[] }>; warnings: Array<{ profile: string; warnings: ProfileIssue[] }> } => {
  const profiles: DocumentProfile[] = [];
  const issues: Array<{ profile: string; issues: ProfileIssue[] }> = [];
  const warnings: Array<{ profile: string; warnings: ProfileIssue[] }> = [];

  for (const raw of rawProfiles) {
    const result = validateProfile(raw);
    const id =
      isRecord(raw) && typeof raw.id === 'string' ? raw.id : '<неизвестный профиль>';
    if (result.issues.length > 0 || !result.profile) {
      issues.push({ profile: id, issues: result.issues });
      continue;
    }
    profiles.push(result.profile);
    if (result.warnings.length > 0) warnings.push({ profile: id, warnings: result.warnings });
  }

  return { profiles, issues, warnings };
};

/** Registry with id / slug / alias lookup — no document-specific code paths. */
export const createProfileRegistry = (profiles: readonly DocumentProfile[]): ProfileRegistry => {
  const byKey = new Map<string, DocumentProfile>();
  for (const profile of profiles) {
    byKey.set(profile.id, profile);
    for (const alias of profile.aliases ?? []) {
      if (!byKey.has(alias)) byKey.set(alias, profile);
    }
    if (profile.seo?.slug && !byKey.has(profile.seo.slug)) byKey.set(profile.seo.slug, profile);
    for (const alias of profile.seo?.aliases ?? []) {
      if (!byKey.has(alias)) byKey.set(alias, profile);
    }
  }
  return {
    list: () => [...new Set(byKey.values())],
    get: (idOrAlias: string) => byKey.get(idOrAlias) ?? byKey.get(idOrAlias.toLowerCase()),
    has: (idOrAlias: string) => byKey.has(idOrAlias) || byKey.has(idOrAlias.toLowerCase()),
  };
};

export const DEFAULT_SEVERITY: Severity = 'error';

/** Applies defaults to a rule declared in a profile. */
export const resolveRule = (profile: DocumentProfile, checkId: string): ResolvedRule => {
  const rule = profile.rules[checkId];
  return {
    id: checkId,
    enabled: rule?.enabled ?? false,
    severity: rule?.severity ?? DEFAULT_SEVERITY,
    params: rule?.params ?? {},
    ...(rule?.official ? { official: rule.official } : {}),
    ...(rule?.measurement ? { measurement: rule.measurement } : {}),
    ...(rule?.policy ? { policy: rule.policy } : {}),
  };
};
